/**
 * Canonical UI primitives. New code should import from here so the
 * layout / spacing / focus / colour system stays in sync across pages.
 *
 *   import { Button, IconButton, Input, Toolbar, FilterChip } from "@/components/ui";
 *
 * Domain-specific components (Panel, Modal, StatusBadge, DataTable)
 * still live at the top level of `components/` — they pre-date this
 * module and are already canonical there. The `Badge` re-export keeps
 * the StatusBadge import path stable while giving new code a familiar
 * name.
 */
export { Button } from "./button";
export type { ButtonProps, ButtonVariant, ButtonSize } from "./button";
export { Checkbox } from "./checkbox";
export type { CheckboxProps } from "./checkbox";
export { IconButton } from "./icon-button";
export type { IconButtonProps, IconButtonVariant, IconButtonSize } from "./icon-button";
export { Input } from "./input";
export type { InputProps, InputSize } from "./input";
export { SearchInput } from "./search-input";
export type { SearchInputProps } from "./search-input";
export { Select } from "./select";
export type { SelectProps } from "./select";
export { Textarea } from "./textarea";
export type { TextareaProps } from "./textarea";
export { Field, FieldLabel } from "./field";
export { Toolbar } from "./toolbar";
export { FilterChip } from "./filter-chip";
export type { FilterChipProps, FilterChipTone } from "./filter-chip";
export { Segmented } from "./segmented";
export type { SegmentedOption, SegmentedProps } from "./segmented";
export { SectionHeader } from "./section-header";
export { PageSection, FormSection } from "./page-section";
export { StatusPanel } from "./status-panel";
export type { StatusPanelTone } from "./status-panel";
export { EmptyState } from "./empty-state";
export { LoadingState } from "./loading-state";
export { ErrorState } from "./error-state";
export { Skeleton, SkeletonRow } from "./skeleton";
export {
  SkeletonLine, SkeletonText, SkeletonField, SkeletonSection, SkeletonTable, SkeletonPill,
} from "./skeleton-parts";
export { FadeIn, StablePresence, ReservedSlot, Collapse } from "./motion";
export { Divider } from "./divider";
export { Kbd } from "./kbd";
export { Badge } from "./badge";
export type { BadgeProps, BadgeSeverity, BadgeState, StatusTone } from "./badge";
export {
  OperatorPage,
  PANE_DEFAULTS,
  PANE_MOBILE_BREAKPOINT,
  useResizablePane,
} from "./operator-page";
export type { OperatorPageProps } from "./operator-page";
export { SplitView } from "./split-view";
export type { SplitViewProps, SplitViewVariant } from "./split-view";
export { ActionButton } from "./action-button";
export type { ActionButtonProps } from "./action-button";
export { ActionMenu } from "./action-menu";
export type { ActionMenuItem, ActionMenuProps } from "./action-menu";
export { ConfirmAction } from "./confirm-action";
export type { ConfirmActionProps } from "./confirm-action";
export { ToastProvider, useToast, createNoopToastApi } from "./toast";
export type { ToastKind } from "./toast";
export { RelativeTime } from "./relative-time";
export type { RelativeTimeProps } from "./relative-time";
export { CreatedBy, formatActorToken } from "./created-by";
export type { CreatedByProps } from "./created-by";
export { RowMeta } from "./row-meta";
export type { RowMetaProps, RowMetaItemProps } from "./row-meta";
export { Drawer } from "./drawer";
export {
  ConfidenceIndicator, tierForConfidence, CONFIDENCE_THRESHOLDS,
  type ConfidenceTier, type ConfidenceIndicatorProps,
} from "./confidence-indicator";
export { RiskBadge, type RiskLevel, type RiskBadgeProps } from "./risk-badge";
export { DryRunBadge } from "./dry-run-badge";
export {
  assessIssueRisk, assessPatchRisk,
  type RecommendedAction, type RecommendedActionResult, type PatchRiskInput,
} from "@/lib/automation/recommended-action";
export type { ToastUndo, ToastOptions } from "./toast";
export {
  ACTIONS as ACTION_CATALOG,
  getAction,
  type ActionKey,
  type ActionTier,
  type ActionDescriptor,
} from "@/lib/actions-catalog/catalog";
